import {ProductOptionType} from "../../definitions/types";
import {ProductOptionModel} from "./productOptionModel";

export class ProductOptionsModel {
    private readonly _productId: number;
    private readonly _options: ProductOptionModel[];

    constructor(public readonly productId: number, public readonly productOptionTypes: ProductOptionType[]) {
        this._productId = productId;
        this._options = productOptionTypes.map((productOption) => new ProductOptionModel(productOption));
    }

    /**
     * @method options
     * @description Returns the options of the product.
     * @returns {ProductOptionModel[]}
     */
    public get options(): ProductOptionModel[] {
        return this._options;
    }

    /**
     * @method count
     * @description Returns the number of options.
     * @returns {number}
     */
    public get count(): number {
        return this._options.length;
    }

    /**
     * @method hasOptions
     * @description Returns true if the product has options.
     * @returns {boolean}
     */
    public get hasOptions(): boolean {
        return this.count > 0;
    }

    /**
     * @method empty
     * @description Returns true if the product has no options.
     * @returns {boolean}
     */
    public get empty(): boolean {
        return this.count === 0;
    }

    /**
     * @method breakOutEarly
     * @description Returns true if:
     * - There are no options.
     * - If any options do not contain any values.
     * - If each option only has one value with one item.
     * - If there is most one item to choose from.
     * @returns {boolean}
     */
    public get breakOutEarly(): boolean {
        // If there are no options, then we can break out early.
        if (this.empty) return true;

        // If any of the options do not contain any values, then we can break out early.
        if (!this._options.every(option => option.values.length > 0)) return true;

        // If each option only has one value with one item, then we can break out early.
        if (this._options.every(option => option.values.length === 1 && option.values[0].items.length === 1)) return true;

        // If there is most one item to choose from, then we can break out early.
        return this._options
            .flatMap((option) => option.values)
            .flatMap((value) => value.items)
            .length <= 1;
    }
}